import React, { useState } from 'react';
import { TemperatureEquipment } from '../../../types/temperatureEquipment';

interface EquipmentManagerProps {
  equipment: TemperatureEquipment[];
  equipmentType: 'freezer' | 'oven' | 'fridge';
  onAdd: (equipment: Partial<TemperatureEquipment>) => void;
  onUpdate: (id: string, equipment: Partial<TemperatureEquipment>) => void;
  onDeactivate: (id: string) => void;
}

export const EquipmentManager: React.FC<EquipmentManagerProps> = ({
  equipment,
  equipmentType,
  onAdd,
  onUpdate,
  onDeactivate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newEquipment, setNewEquipment] = useState({
    equipmentName: '',
    location: '',
    tempRangeMin: '',
    tempRangeMax: '',
  });
  const [editEquipment, setEditEquipment] = useState({
    equipmentName: '',
    location: '',
    tempRangeMin: '',
    tempRangeMax: '',
  });

  const handleStartAdd = () => {
    setIsAddingNew(true);
    setEditingId(null);
    setNewEquipment({ equipmentName: '', location: '', tempRangeMin: '', tempRangeMax: '' });
  };

  const handleSaveNew = () => {
    if (!newEquipment.equipmentName.trim()) return;
    onAdd({
      equipmentName: newEquipment.equipmentName.trim(),
      location: newEquipment.location.trim(),
      equipmentType,
      tempRangeMin: newEquipment.tempRangeMin ? Number(newEquipment.tempRangeMin) : undefined,
      tempRangeMax: newEquipment.tempRangeMax ? Number(newEquipment.tempRangeMax) : undefined,
    });
    setIsAddingNew(false);
    setNewEquipment({ equipmentName: '', location: '', tempRangeMin: '', tempRangeMax: '' });
  };

  const handleStartEdit = (eq: TemperatureEquipment) => {
    setEditingId(eq.id);
    setIsAddingNew(false);
    setEditEquipment({
      equipmentName: eq.equipmentName,
      location: eq.location || '',
      tempRangeMin: eq.tempRangeMin != null ? String(eq.tempRangeMin) : '',
      tempRangeMax: eq.tempRangeMax != null ? String(eq.tempRangeMax) : '',
    });
  };

  const handleSaveEdit = () => {
    if (!editingId || !editEquipment.equipmentName.trim()) return;
    onUpdate(editingId, {
      equipmentName: editEquipment.equipmentName.trim(),
      location: editEquipment.location.trim(),
      tempRangeMin: editEquipment.tempRangeMin ? Number(editEquipment.tempRangeMin) : undefined,
      tempRangeMax: editEquipment.tempRangeMax ? Number(editEquipment.tempRangeMax) : undefined,
    });
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Deactivate this equipment? It will no longer appear in the dropdown.')) {
      onDeactivate(id);
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'background-color 0.2s',
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '13px',
    width: '100%',
    boxSizing: 'border-box',
  };

  const renderForm = (
    values: typeof newEquipment,
    onChange: (field: string, value: string) => void,
    onSave: () => void,
    onCancel: () => void
  ) => (
    <tr>
      <td style={{ padding: '8px' }}>
        <input
          style={inputStyle}
          placeholder="Equipment name"
          value={values.equipmentName}
          onChange={(e) => onChange('equipmentName', e.target.value)}
        />
      </td>
      <td style={{ padding: '8px' }}>
        <input
          style={inputStyle}
          placeholder="Location"
          value={values.location}
          onChange={(e) => onChange('location', e.target.value)}
        />
      </td>
      <td style={{ padding: '8px' }}>
        <input
          style={{ ...inputStyle, width: '80px' }}
          type="number"
          placeholder="Min"
          value={values.tempRangeMin}
          onChange={(e) => onChange('tempRangeMin', e.target.value)}
        />
      </td>
      <td style={{ padding: '8px' }}>
        <input
          style={{ ...inputStyle, width: '80px' }}
          type="number"
          placeholder="Max"
          value={values.tempRangeMax}
          onChange={(e) => onChange('tempRangeMax', e.target.value)}
        />
      </td>
      <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
        <button
          style={{ ...buttonStyle, backgroundColor: '#4CAF50', color: 'white', marginRight: '6px' }}
          onClick={onSave}
        >
          Save
        </button>
        <button
          style={{ ...buttonStyle, backgroundColor: '#757575', color: 'white' }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </td>
    </tr>
  );

  return (
    <div style={{ marginBottom: '16px' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          ...buttonStyle,
          backgroundColor: isOpen ? '#e0e0e0' : '#f5f5f5',
          color: '#333',
          border: '1px solid #ccc',
          marginBottom: isOpen ? '12px' : '0',
        }}
      >
        {isOpen ? 'Hide' : 'Manage'} Equipment
      </button>

      {isOpen && (
        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, fontSize: '1rem' }}>
              {equipmentType.charAt(0).toUpperCase() + equipmentType.slice(1)} Equipment
            </h4>
            {!isAddingNew && (
              <button
                style={{ ...buttonStyle, backgroundColor: '#4CAF50', color: 'white' }}
                onClick={handleStartAdd}
              >
                + Add Equipment
              </button>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Name</th>
                <th style={{ padding: '8px' }}>Location</th>
                <th style={{ padding: '8px' }}>Min Temp (°F)</th>
                <th style={{ padding: '8px' }}>Max Temp (°F)</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isAddingNew &&
                renderForm(
                  newEquipment,
                  (field, value) => setNewEquipment((prev) => ({ ...prev, [field]: value })),
                  handleSaveNew,
                  () => setIsAddingNew(false)
                )}
              {equipment.map((eq) =>
                editingId === eq.id ? (
                  renderForm(
                    editEquipment,
                    (field, value) => setEditEquipment((prev) => ({ ...prev, [field]: value })),
                    handleSaveEdit,
                    () => setEditingId(null)
                  )
                ) : (
                  <tr key={eq.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{eq.equipmentName}</td>
                    <td style={{ padding: '8px' }}>{eq.location || '—'}</td>
                    <td style={{ padding: '8px' }}>{eq.tempRangeMin != null ? `${eq.tempRangeMin}°F` : '—'}</td>
                    <td style={{ padding: '8px' }}>{eq.tempRangeMax != null ? `${eq.tempRangeMax}°F` : '—'}</td>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                      <button
                        style={{ ...buttonStyle, backgroundColor: '#2196F3', color: 'white', marginRight: '6px' }}
                        onClick={() => handleStartEdit(eq)}
                      >
                        Edit
                      </button>
                      <button
                        style={{ ...buttonStyle, backgroundColor: '#f44336', color: 'white' }}
                        onClick={() => handleDelete(eq.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              )}
              {equipment.length === 0 && !isAddingNew && (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No equipment added yet. Click "+ Add Equipment" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
